// src/components/FulfillOrder/FulfillOrder.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { 
  RiArrowLeftLine, 
  RiCheckLine, 
  RiFlagLine, 
  RiErrorWarningLine, 
  RiSubtractLine, 
  RiAddLine, 
  RiLoaderLine 
} from 'react-icons/ri';
import './FulfillOrder.css';
import { BASE_URL } from '../utils/constants';

const FulfillOrder = () => {
  const { reference } = useParams(); // from URL: /fulfill/:reference
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch real order data
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${BASE_URL}/api/orders/${reference}`);
        if (!response.ok) {
          throw new Error(`Failed to load order: ${response.status}`);
        }

        const data = await response.json();
        setOrder(data);

        // Initialize quantities from real items
        const initialQtys = {};
        data.items.forEach((item, index) => {
          initialQtys[`item${index}`] = item.quantity; // start with required qty
        });
        setQuantities(initialQtys);
      } catch (err) {
        console.error('Fulfill fetch error:', err);
        setError('Unable to load order for fulfillment.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [reference]);

  const updateQuantity = (itemId, delta) => {
    setQuantities(prev => ({
      ...prev,
      [itemId]: Math.max(0, (prev[itemId] || 0) + delta)
    }));
  };

  const handleMarkAllPicked = async () => {
    if (!order) return;
    // TODO: PATCH to update status to "processing" + quantities if changed
    alert(`All items marked picked for ${order.reference}. Ready for packing!`);
    // Example future call:
    // await fetch(`/api/orders/${order.reference}/fulfillment`, {
    //   method: 'PATCH',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ deliveryStatus: 'processing', items: updatedItems })
    // });
  };

  const handleContinueToPacking = () => {
    // TODO: Navigate to Packing page or update status
    alert(`Continuing to packing for ${order.reference}`);
    // navigate(`/pack/${reference}`);
  };

  const handleReportIssue = () => {
    // TODO: Open issue modal or send report
    alert(`Issue reported for order ${order.reference}`);
  };

  if (loading) {
    return (
      <div className="fulfill-order-page">
        <div className="loading">
          <RiLoaderLine className="spin" size={32} /> Loading order...
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="fulfill-order-page">
        <div className="error-message">{error || 'Order not found'}</div>
        <button className="back-btn" onClick={() => navigate('/warehouse')}>
          <RiArrowLeftLine /> Back to Queue
        </button>
      </div>
    );
  }

  const totalItems = order.items.length;
  const totalRequired = order.items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <div className="fulfill-order-page">
      <header className="mobile-header">
        <button className="back-btn" onClick={() => navigate('/warehouse')}>
          <RiArrowLeftLine size={22} />
        </button>
        <div className="header-main">
          <div className="order-title-area">
            <h1>{order.reference}</h1>
            <span className={`priority-pill ${order.deliveryStatus}`}>
              {order.deliveryStatus.toUpperCase()}
            </span>
          </div>
        </div>
        <button className="flag-btn">
          <RiFlagLine size={22} />
        </button>
      </header>

      <main className="fulfill-content">
        <section className="order-meta">
          <div className="meta-item">
            <span className="label">Customer:</span> 
            {order.accountName || order.deliveryAddress?.fullName || 'Unknown'}
          </div>
          <div className="meta-item">
            <span className="label">Placed:</span> {new Date(order.createdAt).toLocaleString()}
          </div>
          <div className="meta-item warning">
            <RiErrorWarningLine size={16} />
            {order.deliveryStatus === 'pending' ? 'Ready for fulfillment' : 'In progress'}
          </div>
        </section>

        <section className="items-to-pick">
          <h2>Items to Pick ({totalItems} items, {totalRequired} total qty)</h2>

          {order.items.map((item, index) => {
            const itemId = `item${index}`;
            const currentQty = quantities[itemId] || item.quantity;

            return (
              <div key={itemId} className={`pick-item-card ${currentQty === 0 ? 'out-of-stock' : ''}`}>
                <div className="item-main-info">
                  <h3 className="item-name">{item.name}</h3>
                  <div className="sku-location">
                    <span className="sku">SKU: {item.sku || 'N/A'}</span>
                    {/* Location would come from inventory DB later */}
                    <span className="location">Location: Aisle X • Bin Y</span>
                  </div>
                  {item.notes && <p className="item-note">Note: {item.notes}</p>}
                </div>

                <div className="quantity-control-area">
                  <div className="required-qty">
                    Required: <strong>{item.quantity}</strong>
                  </div>

                  <div className="qty-selector">
                    <button
                      className="qty-btn minus"
                      onClick={() => updateQuantity(itemId, -1)}
                    >
                      <RiSubtractLine />
                    </button>
                    <span className="current-qty">{currentQty}</span>
                    <button
                      className="qty-btn plus"
                      onClick={() => updateQuantity(itemId, 1)}
                    >
                      <RiAddLine />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        <div className="action-bar">
          <button className="mark-all-picked-btn" onClick={handleMarkAllPicked}>
            <RiCheckLine size={18} />
            Mark All Picked
          </button>
          <button className="continue-btn" onClick={handleContinueToPacking}>
            Continue to Packing →
          </button>
        </div>

        <button className="report-issue-btn" onClick={handleReportIssue}>
          <RiErrorWarningLine size={16} />
          Report Issue / Missing Item
        </button>
      </main>
    </div>
  );
};

export default FulfillOrder;
// import React, { useState } from 'react';
// import {
//   RiArrowLeftLine,
//   RiCheckLine,
//   RiFlagLine,
//   RiErrorWarningLine,     // from previous fix
//   RiSubtractLine,           // from previous fix (minus)
//   RiAddLine                 // ← replaced RiPlusLine (does not exist) with this
// } from 'react-icons/ri';

// import './FulfillOrder.css';

// const FulfillOrder = () => {
//   const [quantities, setQuantities] = useState({
//     'item1': 1,
//     'item2': 2,
//     'item3': 1,
//   });

//   const updateQuantity = (itemId, delta) => {
//     setQuantities(prev => ({
//       ...prev,
//       [itemId]: Math.max(0, (prev[itemId] || 0) + delta),
//     }));
//   };

//   const mockItems = [
//     {
//       id: 'item1',
//       name: 'Wireless Noise-Cancelling Headphones',
//       sku: 'HEAD-ANC-001',
//       location: 'Aisle C3 • Bin 14',
//       required: 2,
//       notes: 'Black color preferred',
//     },
//     {
//       id: 'item2',
//       name: 'USB-C Fast Charging Cable (2m)',
//       sku: 'CABLE-USBC-200',
//       location: 'Aisle D1 • Bin 22',
//       required: 3,
//       notes: '',
//       outOfStock: false,
//     },
//     {
//       id: 'item3',
//       name: 'Portable Power Bank 20000mAh',
//       sku: 'PBANK-20000',
//       location: 'Aisle B4 • Bin 08',
//       required: 1,
//       notes: 'Customer requested green variant if available',
//       outOfStock: true,
//     },
//   ];

//   return (
//     <div className="fulfill-order-page">
//       <header className="mobile-header">
//         <button className="back-btn">
//           <RiArrowLeftLine size={22} />
//         </button>
//         <div className="header-main">
//           <div className="order-title-area">
//             <h1>#WH-2024-001289</h1>
//             <span className="priority-pill urgent">URGENT</span>
//           </div>
//         </div>
//         <button className="flag-btn">
//           <RiFlagLine size={22} />
//         </button>
//       </header>

//       <main className="fulfill-content">
//         <section className="order-meta">
//           <div className="meta-item">
//             <span className="label">Customer:</span> Sophia Martinez
//           </div>
//           <div className="meta-item">
//             <span className="label">Placed:</span> 42 minutes ago
//           </div>
//           <div className="meta-item warning">
//             <RiErrorWarningLine size={16} />
//             High priority – due in <strong>1 hour 15 min</strong>
//           </div>
//         </section>

//         <section className="items-to-pick">
//           <h2>Items to Pick ({mockItems.length})</h2>

//           {mockItems.map(item => (
//             <div key={item.id} className={`pick-item-card ${item.outOfStock ? 'out-of-stock' : ''}`}>
//               <div className="item-main-info">
//                 <h3 className="item-name">{item.name}</h3>
//                 <div className="sku-location">
//                   <span className="sku">SKU: {item.sku}</span>
//                   <span className="location">{item.location}</span>
//                 </div>
//                 {item.notes && (
//                   <p className="item-note">Note: {item.notes}</p>
//                 )}
//               </div>

//               <div className="quantity-control-area">
//                 <div className="required-qty">
//                   Required: <strong>{item.required}</strong>
//                 </div>

//                 {!item.outOfStock ? (
//                   <div className="qty-selector">
//                     <button
//                       className="qty-btn minus"
//                       onClick={() => updateQuantity(item.id, -1)}
//                     >
//                       <RiSubtractLine />
//                     </button>
//                     <span className="current-qty">
//                       {quantities[item.id] || 0}
//                     </span>
//                     <button
//                       className="qty-btn plus"
//                       onClick={() => updateQuantity(item.id, 1)}
//                     >
//                       <RiAddLine />    {/* ← updated here */}
//                     </button>
//                   </div>
//                 ) : (
//                   <button className="out-of-stock-btn">
//                     Out of Stock
//                   </button>
//                 )}
//               </div>
//             </div>
//           ))}
//         </section>

//         <div className="action-bar">
//           <button className="mark-all-picked-btn">
//             <RiCheckLine size={18} />
//             Mark All Picked
//           </button>
//           <button className="continue-btn">
//             Continue to Packing →
//           </button>
//         </div>

//         <button className="report-issue-btn">
//           <RiErrorWarningLine size={16} />
//           Report Issue / Missing Item
//         </button>
//       </main>
//     </div>
//   );
// };

// export default FulfillOrder;
// import React, { useState } from 'react';
// import { RiArrowLeftLine, RiCheckLine, RiFlagLine, RiAlertTriangleLine, RiMinusLine, RiPlusLine } from 'react-icons/ri';
// import './FulfillOrder.css';

// const FulfillOrder = () => {
//   const [quantities, setQuantities] = useState({
//     'item1': 1,
//     'item2': 2,
//     'item3': 1,
//   });

//   const updateQuantity = (itemId, delta) => {
//     setQuantities(prev => ({
//       ...prev,
//       [itemId]: Math.max(0, (prev[itemId] || 0) + delta),
//     }));
//   };

//   const mockItems = [
//     {
//       id: 'item1',
//       name: 'Wireless Noise-Cancelling Headphones',
//       sku: 'HEAD-ANC-001',
//       location: 'Aisle C3 • Bin 14',
//       required: 2,
//       notes: 'Black color preferred',
//     },
//     {
//       id: 'item2',
//       name: 'USB-C Fast Charging Cable (2m)',
//       sku: 'CABLE-USBC-200',
//       location: 'Aisle D1 • Bin 22',
//       required: 3,
//       notes: '',
//       outOfStock: false,
//     },
//     {
//       id: 'item3',
//       name: 'Portable Power Bank 20000mAh',
//       sku: 'PBANK-20000',
//       location: 'Aisle B4 • Bin 08',
//       required: 1,
//       notes: 'Customer requested green variant if available',
//       outOfStock: true,
//     },
//   ];

//   return (
//     <div className="fulfill-order-page">
//       <header className="mobile-header">
//         <button className="back-btn">
//           <RiArrowLeftLine size={22} />
//         </button>
//         <div className="header-main">
//           <div className="order-title-area">
//             <h1>#WH-2024-001289</h1>
//             <span className="priority-pill urgent">URGENT</span>
//           </div>
//         </div>
//         <button className="flag-btn">
//           <RiFlagLine size={22} />
//         </button>
//       </header>

//       <main className="fulfill-content">
//         <section className="order-meta">
//           <div className="meta-item">
//             <span className="label">Customer:</span> Sophia Martinez
//           </div>
//           <div className="meta-item">
//             <span className="label">Placed:</span> 42 minutes ago
//           </div>
//           <div className="meta-item warning">
//             <RiAlertTriangleLine size={16} />
//             High priority – due in <strong>1 hour 15 min</strong>
//           </div>
//         </section>

//         <section className="items-to-pick">
//           <h2>Items to Pick ({mockItems.length})</h2>

//           {mockItems.map(item => (
//             <div key={item.id} className={`pick-item-card ${item.outOfStock ? 'out-of-stock' : ''}`}>
//               <div className="item-main-info">
//                 <h3 className="item-name">{item.name}</h3>
//                 <div className="sku-location">
//                   <span className="sku">SKU: {item.sku}</span>
//                   <span className="location">{item.location}</span>
//                 </div>
//                 {item.notes && (
//                   <p className="item-note">Note: {item.notes}</p>
//                 )}
//               </div>

//               <div className="quantity-control-area">
//                 <div className="required-qty">
//                   Required: <strong>{item.required}</strong>
//                 </div>

//                 {!item.outOfStock ? (
//                   <div className="qty-selector">
//                     <button
//                       className="qty-btn minus"
//                       onClick={() => updateQuantity(item.id, -1)}
//                     >
//                       <RiMinusLine />
//                     </button>
//                     <span className="current-qty">
//                       {quantities[item.id] || 0}
//                     </span>
//                     <button
//                       className="qty-btn plus"
//                       onClick={() => updateQuantity(item.id, 1)}
//                     >
//                       <RiPlusLine />
//                     </button>
//                   </div>
//                 ) : (
//                   <button className="out-of-stock-btn">
//                     Out of Stock
//                   </button>
//                 )}
//               </div>
//             </div>
//           ))}
//         </section>

//         <div className="action-bar">
//           <button className="mark-all-picked-btn">
//             <RiCheckLine size={18} />
//             Mark All Picked
//           </button>
//           <button className="continue-btn">
//             Continue to Packing →
//           </button>
//         </div>

//         <button className="report-issue-btn">
//           <RiAlertTriangleLine size={16} />
//           Report Issue / Missing Item
//         </button>
//       </main>
//     </div>
//   );
// };

// export default FulfillOrder;